import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { LearningObjective } from '../../../shared-pages/subject-details/api-subject-details';
import { ToastrService } from 'ngx-toastr';
import { FileUploadService } from '../edit-question-block/file-upload.service';
import { Subject, Subscription, firstValueFrom } from 'rxjs';
import { EventObj } from '@tinymce/tinymce-angular/editor/Events';
import { TranslateService } from '@ngx-translate/core';

type Uuid = string;
type BlobUrl = string;

interface TinyOptionsInput {
  placeholder?: string;
  height?: number;
  disabled?: boolean;
  menubar?: string;
  statusbar?: boolean;
}

@Component({
  selector: 'curr-tiny-editor-v2',
  templateUrl: './tiny-editor-v2.component.html',
  styleUrls: ['./tiny-editor-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TinyEditorV2Component implements OnInit, OnDestroy, OnChanges {
  @Input() options: TinyOptionsInput = {};
  @Input() value = '';
  @Input() isHigherBtnVisible = false;
  @Input() formGroup: FormGroup | null = null;
  @Input() controlName = '';
  @Input() topic: LearningObjective | null = null;
  @Input() needOnChange = true;
  @Input() showTopSection = true;

  @Input() title: string;
  @Input() subtitle: string;
  @Input() hint: string;
  @Input() isEditorMode = true;

  @Output() valueChange = new EventEmitter<string | null>();
  @Output() onFocusOut = new EventEmitter<EventObj<FocusEvent>>();
  @Output() onLoaded = new EventEmitter<string>();
  @Output() onImageUploaded = new EventEmitter<string>();

  key = environment.tinyEditorKey;

  editorOptions: any = {};
  isEditorReady = false;
  valueForTinyEditor = '';
  readModeValue = '';
  hasValue = false;
  private change$ = new Subject<string>();
  private subs = new Subscription();

  private uuidToBlobUrl: Record<Uuid, BlobUrl> = {};

  constructor(
    private toastr: ToastrService,
    private fb: FormBuilder,
    private fileUploadService: FileUploadService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  // Keep external changes in sync
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.needOnChange) return;

    if (changes.value && !changes.value.firstChange) {
      const newHtml = changes.value.currentValue || '';

      if (this.isEditorMode) {
        if (this.isEditorReady) {
          this.valueForTinyEditor = this.replaceUuidsWithBlobUrls(newHtml);
          this.cleanRemovedImages(newHtml);
          this.cdr.detectChanges();
        }
      } else {
        this.prepareInitialContentWithBlobUrls(newHtml).then(html => {
          this.readModeValue = html || '';
          this.hasValue = html && html !== '';
          this.cdr.detectChanges();
        });
      }
    }

    if (changes.isEditorMode && changes.isEditorMode.currentValue === false) {
      this.prepareInitialContentWithBlobUrls(this.value).then(html => {
        this.readModeValue = html;
        this.hasValue = html && html !== '';
        this.cdr.detectChanges();
      });
    }

    // if (this.formGroup && this.controlName && this.formGroup.controls[this.controlName] && changes.value?.firstChange) {
    //   // Initialize value from form if external value is empty
    //   if (!this.value) {
    //     const v = this.formGroup.controls[this.controlName].value || '';
    //     this.value = v;
    //   }
    // }
  }

  ngOnInit(): void {
    this.setEditorLanguage(this.translateService.currentLang);

    this.subs.add(
        this.translateService.onLangChange.subscribe((event) => {
          this.setEditorLanguage(event.lang);

          if (this.isEditorReady) {
            this.isEditorReady = false;
            this.cdr.detectChanges();

            setTimeout(() => {
              this.isEditorMode = false;
              this.cdr.detectChanges();
              this.isEditorMode = true;
              this.cdr.detectChanges();
            });
          }
        })
    );

    // Ensure formGroup/control
    if (!this.formGroup) {
      this.controlName = 'item' + Date.now();
      this.formGroup = this.fb.group({
        [this.controlName]: new FormControl(''),
      });
    }

    // Debounced change propagation
    // this.subs.add(
    //   this.change$.pipe(debounceTime(250), distinctUntilChanged()).subscribe((content) => {
    //     this.emitPersistentValue(content);
    //   })
    // );

    if (!this.isEditorMode) {
      this.prepareInitialContentWithBlobUrls(this.value).then(html => {
        this.readModeValue = html || '';
        this.hasValue = !!html;
        this.cdr.detectChanges();
      });
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    // Revoke all blob URLs
    Object.values(this.uuidToBlobUrl).forEach((u) => {
      try {
        URL.revokeObjectURL(u);
      } catch {}
    });
  }

  onToggleMode(isEditor: boolean) {
    if (!isEditor) {
      this.switchToReadMode();
    }
  }

  private setEditorLanguage(lang: string) {
    const map: Record<string, string> = {
      en: 'en',
      'zh-CN': 'zh_CN',
      zh: 'zh_CN',
      ch: 'zh_CN',
    };
    const chosen = map[lang] || 'en';

    this.editorOptions = {
      ...this.baseEditorOptions(),
      language_url: `/assets/tinymce/langs/${chosen}.js`,
      language: chosen,
    };
  }

  private baseEditorOptions() {
    return {
      id: Date.now(),
      placeholder: this.options.placeholder ?? 'Type here...',
      height: this.options.height ?? 700,
      readonly: this.options.disabled ?? false,
      menubar: this.options.menubar ?? 'file edit view format table help',
      statusbar: this.options.statusbar ?? true,
      outputFormat: 'html',
      browser_spellcheck: true,
      contextmenu: false,
      paste_data_images: true,
      resize: true,
      automatic_uploads: false,
      images_upload_handler: this.imageUploadHandler.bind(this),
      image_title: true,
      file_picker_types: 'image',
      autoresize_bottom_margin: 20,
      extended_valid_elements: 'higher[style]',
      custom_elements: 'higher',
      forced_root_block: 'p',
      content_style: `
        higher { display: inline; }
        img { max-width: 100%; height: auto; display: block; }
      `,
      external_plugins: {
        tiny_mce_wiris: 'https://www.wiris.net/demo/plugins/tiny_mce/plugin.js',
      },
      plugins: [
        'advlist autolink lists link image autoresize charmap print preview anchor',
        'searchreplace visualblocks code fullscreen tiny_mce_wiris',
        'insertdatetime media table paste help wordcount',
      ],
      toolbar: `
        code | undo redo | formatselect | bold italic backcolor |
        image media | alignleft aligncenter alignright alignjustify |
        bullist numlist outdent indent | removeformat |
        tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry
        ${this.isHigherBtnVisible ? '| customHigherButton customClearHigherButton' : ''} | help
      `,
      setup: (editor) => {
        // Custom buttons
        if (this.isHigherBtnVisible) {
          editor.ui.registry.addButton('customHigherButton', {
            icon: 'warning',
            tooltip: 'Make text Higher',
            onAction: () => {
              const node = editor.selection.getContent();
              editor.selection.setContent(
                  `<higher><span style="background-color:#56C7D8;">${node}</span></higher>`
              );
            },
          });

          editor.ui.registry.addButton('customClearHigherButton', {
            icon: 'remove',
            tooltip: 'Remove Higher',
            onAction: () => {
              const content = editor.selection.getContent();
              editor.insertContent(content);
            },
          });
        }
      },
      base_url: '/tinymce',
      suffix: '.min',
    };
  }

  private cleanRemovedImages(currentHtml: string) {
    const stillUsed = this.fileUploadService.getAllImageKeysFromNote(currentHtml) || {};

    for (const uuid of Object.keys(this.uuidToBlobUrl)) {
      if (!stillUsed[uuid]) {
        URL.revokeObjectURL(this.uuidToBlobUrl[uuid]);
        delete this.uuidToBlobUrl[uuid];
      }
    }
  }

  async switchToReadMode() {
    this.readModeValue = await this.prepareInitialContentWithBlobUrls(this.value);
    this.hasValue = this.value && this.value !== '';
    this.cdr.detectChanges();
  }

  async onEditorInitialized() {
    this.isEditorReady = false;

    const persistedValue = this.value || this.formGroup?.controls[this.controlName]?.value || '';
    this.valueForTinyEditor = persistedValue;

    const uuids = Object.keys(this.fileUploadService.getAllImageKeysFromNote(persistedValue) || {});

    for (const uuid of uuids) {
      try {
        const r: any = await firstValueFrom(this.fileUploadService.download(uuid, 'IMAGE'));
        const bytes: string | undefined = r?.data?.bytes;
        if (!bytes) continue;

        const blob = this.b64toBlob(bytes, 'image/png');
        const blobUrl = URL.createObjectURL(blob);
        this.uuidToBlobUrl[uuid] = blobUrl;

        this.valueForTinyEditor = this.safeReplaceAll(this.valueForTinyEditor, uuid, blobUrl);
      } catch (e) {
        this.toastr.warning(`Image ${uuid} not loaded`, 'Warning');
      }
    }

    this.isEditorReady = true;
    this.onLoaded.emit(this.valueForTinyEditor);
    this.cdr.detectChanges();
  }

  onKeyPress(event: any) {
    const content = event.editor.getContent();
    this.valueForTinyEditor = content;
    this.cleanRemovedImages(content);
    this.value = content;
    this.change$.next(content);
    this.cdr.detectChanges();
  }

  onEditorChange(event: any) {
    // const content = event.editor.getContent();
    // this.valueForTinyEditor = content;
    // this.cleanRemovedImages(content);
    // this.change$.next(content);
  }

  onBlur(e: EventObj<FocusEvent>) {
    this.emitPersistentValue(this.valueForTinyEditor);
    this.onFocusOut.emit(e);
  }

  private imageUploadHandler(blobInfo, success, failure, progress) {
    // Note: TinyMCE v5 handler signature: (blobInfo, success, failure, progress)
    const blob = blobInfo.blob();

    this.fileUploadService.upload(blob, 'IMAGE').subscribe({
      next: async (resp: { uuidName: string; oldName: string; state: string }[]) => {
        const i = resp?.[0];
        if (!i?.uuidName) {
          failure('Upload failed: no uuidName received');
          return;
        }

        try {
          // Download bytes to be able to show the image immediately as blob:
          const r: any = await firstValueFrom(this.fileUploadService.download(i.uuidName, 'IMAGE'));
          const bytes: string | undefined = r?.data?.bytes;
          if (!bytes) {
            failure('Download failed: empty bytes');
            return;
          }

          const blobFromServer = this.b64toBlob(bytes, 'image/png');
          const blobUrl = URL.createObjectURL(blobFromServer);

          // Map UUID <-> blob URL for later persistence conversion
          this.uuidToBlobUrl[i.uuidName] = blobUrl;

          // Show in editor (fast)
          success(blobUrl);

          // Notify listeners (optional)
          this.onImageUploaded.emit(i.uuidName);
        } catch (err: any) {
          failure(err?.message || 'Error while downloading image');
        }
      },
      error: (err) => failure(err?.msg || 'Upload error'),
    });
  }

  /** Build editor-facing HTML by swapping UUID src -> blob: URL src */
  private async prepareInitialContentWithBlobUrls(html: string): Promise<string> {
    if (!html) return '';
    const uuids = Object.keys(this.fileUploadService.getAllImageKeysFromNote(html) || {});

    for (const uuid of uuids) {
      // Skip already cached
      if (!this.uuidToBlobUrl[uuid]) {
        try {
          const r: any = await firstValueFrom(this.fileUploadService.download(uuid, 'IMAGE'));
          const bytes: string | undefined = r?.data?.bytes;
          if (!bytes) continue;
          const b = this.b64toBlob(bytes, 'image/png');
          this.uuidToBlobUrl[uuid] = URL.createObjectURL(b);
        } catch {
          // Ignore broken images silently; you may toast if needed
        }
      }
      html = this.safeReplaceAll(html, uuid, this.uuidToBlobUrl[uuid]);
    }
    return html;
  }

  /** Emit the persisted version: swap blob: URL src -> UUID src */
  private emitPersistentValue(editorHtml: string) {
    let persisted = editorHtml;

    for (const [uuid, blobUrl] of Object.entries(this.uuidToBlobUrl)) {
      persisted = this.safeReplaceAll(persisted, blobUrl, uuid);
    }

    const normalized: string | null = this.isEmptyHtml(persisted) ? null : persisted;

    this.value = normalized;
    this.valueChange.emit(normalized);

    if (this.formGroup && this.controlName && this.formGroup.controls[this.controlName]) {
      this.formGroup.controls[this.controlName].patchValue(normalized, { emitEvent: false });
    }
  }

  private isEmptyHtml(html: string): boolean {
    if (!html) return true;

    const hasImage = /<img\s+[^>]*src=["'][^"']+["'][^>]*>/i.test(html);
    if (hasImage) return false;

    const stripped = html.replace(/<[^>]+>/g, '').trim();
    return stripped.length === 0;
  }

  /** Convert all UUIDs to blob URLs for displaying in editor (used in ngOnChanges) */
  private replaceUuidsWithBlobUrls(html: string): string {
    let out = html;
    const uuids = Object.keys(this.fileUploadService.getAllImageKeysFromNote(html) || {});
    for (const uuid of uuids) {
      const blobUrl = this.uuidToBlobUrl[uuid];
      if (blobUrl) {
        out = this.safeReplaceAll(out, uuid, blobUrl);
      }
    }
    return out;
  }

  /** Safe global replacement without regex escaping headaches */
  private safeReplaceAll(haystack: string, needle: string, replacement: string): string {
    return haystack.split(needle).join(replacement);
  }

  private b64toBlob(b64Data: string, contentType = '', sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays: Uint8Array[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
  }
}
